import { useLayoutEffect, useMemo, useState } from 'react'

import { SECONDS_PER_HOUR, SECONDS_PER_MINUTE } from 'utils/units'

import type { MuxPlaybackEmbed } from 'utils/videoUrlResolver'

const MINUTE_TIMESTAMP_PARTS = 2
const HOUR_TIMESTAMP_PARTS = 3
const SNAPSHOT_END_PADDING_SECONDS = 0.1
const MIN_MIDPOINT_FRAME_COUNT = 3
const BIN_MIDPOINT = 0.5

const MUX_IMAGE_BASE_URL = 'https://image.mux.com'

type SnapshotMode = 'even' | 'midpoint'

export type MuxStoryboardFrame = {
  endTimeSeconds: number
  imageUrl: string
  sheetHeight: number
  sheetWidth: number
  startTimeSeconds: number
  tileHeight: number
  tileWidth: number
  x: number
  y: number
}

type ParsedStoryboardCue = Omit<MuxStoryboardFrame, 'sheetHeight' | 'sheetWidth'>

const isFinitePositiveNumber = (value: number | undefined): value is number => {
  return value !== undefined && Number.isFinite(value) && value > 0
}

const parseTimestampSeconds = (value: string): number | null => {
  const [timestamp] = value.trim().split(/\s+/)
  const parts = timestamp.split(':')
  if (parts.length < MINUTE_TIMESTAMP_PARTS || parts.length > HOUR_TIMESTAMP_PARTS) {
    return null
  }

  const secondsPart = Number(parts[parts.length - 1])
  const minutesPart = Number(parts[parts.length - MINUTE_TIMESTAMP_PARTS])
  const hoursPart = parts.length === HOUR_TIMESTAMP_PARTS ? Number(parts[0]) : 0
  if (!Number.isFinite(secondsPart) || !Number.isFinite(minutesPart) || !Number.isFinite(hoursPart)) {
    return null
  }

  return hoursPart * SECONDS_PER_HOUR + minutesPart * SECONDS_PER_MINUTE + secondsPart
}

const resolveStoryboardImageUrl = (rawUrl: string, storyboardUrl: string): string | null => {
  const [sourceUrl, fragment] = rawUrl.trim().split('#')
  if (!sourceUrl || !fragment) {
    return null
  }

  try {
    const storyboard = new URL(storyboardUrl)
    const resolved = new URL(sourceUrl, storyboard)
    const storyboardToken = storyboard.searchParams.get('token')
    if (storyboardToken && !resolved.searchParams.has('token')) {
      resolved.searchParams.set('token', storyboardToken)
    }
    return resolved.toString()
  } catch {
    return null
  }
}

const parseXywhFragment = (rawUrl: string): Pick<MuxStoryboardFrame, 'tileHeight' | 'tileWidth' | 'x' | 'y'> | null => {
  const match = /#xywh=(\d+),(\d+),(\d+),(\d+)$/.exec(rawUrl.trim())
  if (!match) {
    return null
  }

  const [, x, y, tileWidth, tileHeight] = match
  return {
    tileHeight: Number(tileHeight),
    tileWidth: Number(tileWidth),
    x: Number(x),
    y: Number(y),
  }
}

export const buildMuxStoryboardUrl = (mux?: MuxPlaybackEmbed): string | null => {
  const storyboardToken = mux?.tokens?.storyboard
  if (!mux?.playbackId || !storyboardToken) {
    return null
  }

  const url = new URL(`${MUX_IMAGE_BASE_URL}/${mux.playbackId}/storyboard.vtt`)
  url.searchParams.set('token', storyboardToken)
  return url.toString()
}

export const parseMuxStoryboardVtt = (vtt: string, storyboardUrl: string): MuxStoryboardFrame[] => {
  const cues: ParsedStoryboardCue[] = []
  const lines = vtt.split(/\r?\n/)

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trim()
    if (!line.includes('-->')) {
      continue
    }

    const [startRaw, endRaw] = line.split('-->').map(part => part.trim())
    const startTimeSeconds = parseTimestampSeconds(startRaw)
    const endTimeSeconds = parseTimestampSeconds(endRaw)
    const imageLine = index + 1 < lines.length ? lines[index + 1].trim() : ''
    const imageUrl = imageLine ? resolveStoryboardImageUrl(imageLine, storyboardUrl) : null
    const xywh = imageLine ? parseXywhFragment(imageLine) : null

    if (
      startTimeSeconds === null ||
      endTimeSeconds === null ||
      endTimeSeconds <= startTimeSeconds ||
      !imageUrl ||
      !xywh ||
      xywh.tileWidth <= 0 ||
      xywh.tileHeight <= 0
    ) {
      continue
    }

    cues.push({
      endTimeSeconds,
      imageUrl,
      startTimeSeconds,
      ...xywh,
    })
  }

  const sheetSizes = new Map<string, { sheetHeight: number; sheetWidth: number }>()
  for (const cue of cues) {
    const current = sheetSizes.get(cue.imageUrl) ?? { sheetHeight: 0, sheetWidth: 0 }
    sheetSizes.set(cue.imageUrl, {
      sheetHeight: Math.max(current.sheetHeight, cue.y + cue.tileHeight),
      sheetWidth: Math.max(current.sheetWidth, cue.x + cue.tileWidth),
    })
  }

  return cues.map(cue => ({
    ...cue,
    ...(sheetSizes.get(cue.imageUrl) ?? {
      sheetHeight: cue.tileHeight,
      sheetWidth: cue.tileWidth,
    }),
  }))
}

export const buildStoryboardSnapshotTimes = (
  durationSeconds: number | undefined,
  frameCount: number,
  mode: SnapshotMode
): number[] => {
  if (!isFinitePositiveNumber(durationSeconds) || frameCount <= 0) {
    return []
  }

  const safeDuration = Math.max(SNAPSHOT_END_PADDING_SECONDS, durationSeconds - SNAPSHOT_END_PADDING_SECONDS)
  if (mode === 'even') {
    return Array.from({ length: frameCount }, (_, index) => (index / Math.max(1, frameCount - 1)) * safeDuration)
  }

  const count = Math.min(frameCount, Math.max(MIN_MIDPOINT_FRAME_COUNT, Math.ceil(durationSeconds)))
  return Array.from({ length: count }, (_, index) => {
    const fraction = (index + BIN_MIDPOINT) / count
    return Math.min(Math.floor(fraction * durationSeconds), Math.max(0, Math.floor(durationSeconds) - 1))
  })
}

const findStoryboardFrameAtTime = (
  frames: MuxStoryboardFrame[],
  timeSeconds: number
): MuxStoryboardFrame | undefined => {
  return frames.find(frame => timeSeconds >= frame.startTimeSeconds && timeSeconds < frame.endTimeSeconds)
}

export const selectStoryboardFrames = ({
  durationSeconds,
  frameCount,
  mode,
  storyboardFrames,
}: {
  durationSeconds?: number
  frameCount: number
  mode: SnapshotMode
  storyboardFrames: MuxStoryboardFrame[]
}): MuxStoryboardFrame[] => {
  const storyboardDuration =
    storyboardFrames.length > 0 ? storyboardFrames[storyboardFrames.length - 1].endTimeSeconds : undefined
  const snapshotTimes = buildStoryboardSnapshotTimes(durationSeconds ?? storyboardDuration, frameCount, mode)
  if (snapshotTimes.length === 0) {
    return []
  }

  const selectedFrames = snapshotTimes.flatMap(timeSeconds => {
    const frame = findStoryboardFrameAtTime(storyboardFrames, timeSeconds)
    return frame ? [frame] : []
  })

  return selectedFrames.length === snapshotTimes.length ? selectedFrames : []
}

export const useMuxStoryboardFrames = ({
  durationSeconds,
  frameCount,
  mode,
  mux,
}: {
  durationSeconds?: number
  frameCount: number
  mode: SnapshotMode
  mux?: MuxPlaybackEmbed
}): MuxStoryboardFrame[] => {
  const storyboardUrl = buildMuxStoryboardUrl(mux)
  const [storyboardFrames, setStoryboardFrames] = useState<MuxStoryboardFrame[]>([])

  useLayoutEffect(() => {
    if (!storyboardUrl) {
      setStoryboardFrames([])
      return
    }

    let isActive = true

    const loadStoryboard = async () => {
      try {
        const response = await fetch(storyboardUrl)
        if (!response.ok) {
          throw new Error('Mux storyboard request failed')
        }

        const vtt = await response.text()
        const parsedFrames = parseMuxStoryboardVtt(vtt, storyboardUrl)
        if (isActive) {
          setStoryboardFrames(parsedFrames)
        }
      } catch {
        if (isActive) {
          setStoryboardFrames([])
        }
      }
    }

    void loadStoryboard()

    return () => {
      isActive = false
    }
  }, [storyboardUrl])

  return useMemo(
    () =>
      selectStoryboardFrames({
        durationSeconds,
        frameCount,
        mode,
        storyboardFrames,
      }),
    [durationSeconds, frameCount, mode, storyboardFrames]
  )
}
